import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { StatusLabel } from '@euler/app/components/StatusLabel';
import { Typography } from '@euler/app/components/typography';
import { TimeElapsedLabel } from '@euler/app/flows/task/pages/detail/components/TimeElapsedLabel';
import { Avatar } from '@euler/components/Avatar';
import { StatusColors } from '@euler/components/Colors';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { VehicleServiceRecord } from '@euler/model';
import { CommonTaskStatus } from '@euler/model/enum';
import { formatDateTime } from '@euler/utils/formatters';
import { LinearGradient } from 'expo-linear-gradient';
import { ReactNode, memo, useCallback } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const ChildRecordListItem = memo(
  ({
    record,
    style,
    onViewDetail,
  }: {
    record: VehicleServiceRecord;
    style?: StyleProp<ViewStyle>;
    onViewDetail?: (record: VehicleServiceRecord) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const onViewDetailPress = useCallback(() => {
      onViewDetail?.(record);
    }, [onViewDetail, record]);

    const headerBgColors: string[] = [StatusColors.Success, '#6bbf6a'];

    return (
      <View
        style={style}
        css={`
          background-color: #fff;
          border-radius: ${rpx(10)}px;
          overflow: hidden;
        `}
      >
        <TouchableOpacity
          onPress={onViewDetailPress}
          css={`
            min-height: ${rpx(44)}px;
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(15)}px;
            flex-direction: row;
            justify-content: space-between;
            flex-wrap: nowrap;
            align-items: center;
          `}
        >
          <LinearGradient
            colors={headerBgColors}
            start={[0, 0]}
            end={[1, 0]}
            style={StyleSheet.absoluteFill}
          />
          <Typography.Text medium size={rpx(18)} color="#fff">
            {record.tags?.join('/')}
          </Typography.Text>
          <LinkButton
            text="查看详情 >"
            onPress={onViewDetailPress}
            textStyle={{
              fontSize: rpx(13),
              color: 'rgba(255, 255, 255, 0.65)',
            }}
          />
        </TouchableOpacity>

        <StackLayout
          divider={
            <View
              css={`
                height: ${StyleSheet.hairlineWidth}px;
                background-color: ${Platform.OS === 'web' ? '#eee' : '#ddd'};
              `}
            />
          }
          css={`
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(15)}px;
            padding-bottom: ${rpx(4)}px;
          `}
        >
          <InfoRow label="服务顾问">
            <StackLayout
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              flexWrap="nowrap"
            >
              <Avatar
                name={record.serviceAgentName}
                width={rpx(18)}
                height={rpx(18)}
                borderRadius={rpx(9)}
              />
              <Typography.Label medium size={rpx(12)} color="#555" ml={rpx(4)}>
                {record.serviceAgentName}
              </Typography.Label>
            </StackLayout>
          </InfoRow>

          <InfoRow label="订单编号" value={record.orderNo} />
          <InfoRow label="工单编号" value={record.taskNo} />
          <InfoRow label="创建时间" value={formatDateTime(record.createdAt)} />
          <InfoRow
            label="完成时间"
            value={formatDateTime(record.finishedAt!)}
          />
          {record.preInspectionStatus === CommonTaskStatus.Finished && (
            <StatusRow label="接车预检" status={record.preInspectionStatus} />
          )}
          {record.inspectionStatus === CommonTaskStatus.Finished && (
            <StatusRow label="常规检测" status={record.inspectionStatus} />
          )}
          {record.constructionStatus === CommonTaskStatus.Finished && (
            <StatusRow label="施工反馈" status={record.constructionStatus} />
          )}
          {record.deliveryCheckStatus === CommonTaskStatus.Finished && (
            <StatusRow label="竣工检验" status={record.deliveryCheckStatus} />
          )}
          {record.samplingCheckStatus === CommonTaskStatus.Finished && (
            <StatusRow label="完工抽检" status={record.samplingCheckStatus} />
          )}
        </StackLayout>
      </View>
    );
  },
);

const StatusRow = memo(
  ({
    label,
    status,
    createdAt,
    startedAt,
    finishedAt,
    style,
  }: {
    label: string;
    status: CommonTaskStatus;
    startedAt?: string | Date | null;
    finishedAt?: string | Date | null;
    createdAt?: string | Date | null;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={style}
        css={`
          padding-top: ${rpx(3)}px;
          padding-bottom: ${rpx(3)}px;
        `}
      >
        <Typography.Label light size={rpx(14)} color="#333">
          {label}
        </Typography.Label>
        <StackLayout
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={rpx(10)}
        >
          <TimeElapsedLabel
            textStyle={{ lineHeight: rpx(20) }}
            style={{ marginTop: 0, opacity: 0.75 }}
            flow={{
              status:
                status === CommonTaskStatus.Finished
                  ? 'finished'
                  : status === CommonTaskStatus.InProgress
                  ? 'inprogress'
                  : 'pending',
              finishedAt,
              startedAt,
              createdAt,
            }}
          />
          <StatusLabel
            status={status}
            textStyle={[
              { fontSize: rpx(11), fontWeight: '500' },
              status === CommonTaskStatus.Pending
                ? { color: '#777' }
                : undefined,
            ]}
            style={[
              {
                height: rpx(18),
                paddingHorizontal: rpx(Platform.OS === 'web' ? 8 : 2),
              },
              status === CommonTaskStatus.Pending
                ? {
                    backgroundColor: '#ddd',
                  }
                : undefined,
            ]}
          />
        </StackLayout>
      </StackLayout>
    );
  },
);

const InfoRow = memo(
  ({
    label,
    value,
    children,
    style,
  }: {
    label: string;
    value?: string;
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={style}
        css={`
          padding-top: ${rpx(3)}px;
          padding-bottom: ${rpx(3)}px;
        `}
      >
        <Typography.Label light size={rpx(14)} color="#333">
          {label}
        </Typography.Label>
        <StackLayout
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={rpx(5)}
        >
          {value ? (
            <Typography.Text medium size={rpx(14)} color="#777">
              {value}
            </Typography.Text>
          ) : null}
          {children}
        </StackLayout>
      </StackLayout>
    );
  },
);
